import { supabase } from './supabase';

// The customer's logo, uploaded per site.
//
// This is not the app's logo. OnHand's own mark is drawn by
// components/brand/OnHandLogo.jsx, from code, and nobody can change it. What
// this file moves is the logo a site uploads for itself.
//
// It lives in two places at once and both matter: the bytes go to the branding
// bucket under the site's own id, and the public URL goes to app_settings so
// every screen can read it without a storage round trip. The namespacing is
// load-bearing - a bare 'logo.png' in a shared bucket was fine with one
// company and silently destructive with two, because the second upload
// overwrote the first and both rows still pointed at the same URL.

export const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ALLOWED = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

// Returns an error message, or null when the file is fine. Kept separate from
// the upload so a screen can refuse a file before it starts a spinner.
export function checkLogoFile(file) {
  if (!file) return 'No file chosen.';
  if (!ALLOWED.includes(file.type)) return 'Please upload a PNG, JPG, SVG, or WebP file.';
  if (file.size > MAX_LOGO_BYTES) return 'That file is larger than 2 MB. Please upload a smaller image.';
  return null;
}

export async function uploadSiteLogo(file, orgId) {
  if (!orgId) throw new Error('Could not tell which site you are in. Reload and try again.');
  const bad = checkLogoFile(file);
  if (bad) throw new Error(bad);

  const ext  = (file.name.split('.').pop() || 'png').toLowerCase();
  const path = orgId + '/logo.' + ext;

  const { error: upErr } = await supabase.storage
    .from('branding')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (upErr) throw upErr;

  // The path is stable across replacements, so without a cache-buster a
  // replaced logo keeps serving the old bytes from the browser cache.
  const { data } = supabase.storage.from('branding').getPublicUrl(path);
  const publicUrl = data.publicUrl + '?t=' + Date.now();

  const { error: setErr } = await supabase
    .from('app_settings')
    .upsert({ key: 'logo_url', value: publicUrl }, { onConflict: 'org_id,key' });
  if (setErr) throw setErr;

  return publicUrl;
}

// The stored bytes are left where they are. Clearing the URL is what takes the
// logo off the screen, and keeping the file means putting it back is a re-save
// rather than a re-upload.
export async function clearSiteLogo() {
  const { error } = await supabase.from('app_settings').update({ value: '' }).eq('key', 'logo_url');
  if (error) throw error;
}
